import React from "react";
import { Link } from "react-router-dom";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";
import { isAllowed } from "helper/acl";
import { isFrontPermissionVisible } from "helper/documentModules";

import PageHeader from "../../Common/PageHeader";
import { buildBreadcrumbs } from "helper/breadcrumbs";
import { ActionIcon, DataTable, Loader } from "../../ui";
import type { DataTableColumn } from "../../ui/DataTable/DataTable";
import type { RawDocumentModule } from "../../../helper/documentModules";
import { withModulesData, type ModulesQueryProps } from "api/queries/useModules";
import {
  withProfileDelegateData,
  type DelegateUser,
  type PermissionItem,
  type ProfileDelegateActionsAdapter,
  type ProfileDelegateQueryProps,
} from "api/queries/useProfileDelegate";

interface ProfileDelegatePageListOwnProps extends WithTranslation {}

interface ProfileDelegatePageListDispatchProps {
  profileDelegateActions: ProfileDelegateActionsAdapter;
}

type ProfileDelegatePageListProps = ProfileDelegatePageListOwnProps &
  ProfileDelegateQueryProps &
  ModulesQueryProps &
  ProfileDelegatePageListDispatchProps;

function ProfileDelegatePageList({
  t,
  modules,
  delegateUsers,
  delegateUsersLoaded,
  message,
  profileDelegateActions,
}: ProfileDelegatePageListProps) {
  React.useEffect(() => {
    profileDelegateActions.loadDelegateUsers();
    profileDelegateActions.loadPermissions();
    profileDelegateActions.messageDelegateUser();
    return () => {
      profileDelegateActions.unloadPermissions();
      profileDelegateActions.unloadDelegateUsers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPermissionLabel = (permission: PermissionItem): string => {
    const currentPermission = (modules || []).find(
      (module) =>
        (module as RawDocumentModule & { front_permission?: string }).front_permission ===
        permission.permission_name
    ) as (RawDocumentModule & { name?: unknown; front_permission?: string }) | undefined;

    if (!currentPermission) return "";

    if (typeof currentPermission.name === "string") {
      return currentPermission.name;
    }

    if (
      currentPermission.name &&
      typeof (currentPermission.name as { value?: string }).value === "string"
    ) {
      return (currentPermission.name as { value: string }).value;
    }

    return currentPermission.slug || currentPermission.front_permission || "";
  };

  const deleteDelegateUser = (id: number | string) => {
    profileDelegateActions.deleteDelegateUser(id);
  };

  const renderPageShell = (
    title: React.ReactNode,
    intro: React.ReactNode,
    content: React.ReactNode
  ) => {
    const breadcrumbs = buildBreadcrumbs(t, "profileDelegate");
    return (
      <div className="Page AccountPage DelegatePageShell">
        <div className="Page-content AccountPage-content">
          <PageHeader breadcrumbs={breadcrumbs} title={title} bannerClassName="AccountBanner" />
          {content}
        </div>
      </div>
    );
  };

  const columns: DataTableColumn<DelegateUser>[] = [
    {
      key: "name",
      label: t("profile.delegate.information"),
      render: (user: DelegateUser) => (
        <>
          <span className="ElementCard-title">
            {user.first_name} {user.last_name}
          </span>
          <p>
            <strong>{t("profile.delegate.label_email")} </strong>
            {user.email}
          </p>
        </>
      ),
    },
    {
      key: "permissions",
      label: t("profile.delegate.modules"),
      render: (user: DelegateUser) => (
        <ul>
          {(user.permissions || []).map((permission, index) => {
            if (
              permission.permission_name === "webservice.log" ||
              permission.permission_name === "webservice.module" ||
              !isFrontPermissionVisible(permission.permission_name) ||
              !permission.slug
            ) {
              return null;
            }
            return <li key={index.toString()}>{getPermissionLabel(permission)}</li>;
          })}
        </ul>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (user: DelegateUser) => (
        <div className="ElementCard-actionBlock">
          <Link
            to={BASE_PATH + "/profile/delegate/edit/" + user.id}
            className="DelegateAction DelegateAction--edit"
            aria-label={t("common.button_update")}
            title={t("common.button_update")}
          >
            <ActionIcon name="edit" />
          </Link>
          <button
            type="button"
            className="DelegateAction DelegateAction--delete"
            disabled={!user.id}
            onClick={() => deleteDelegateUser(user.id!)}
            aria-label={t("common.button_delete")}
            title={t("common.button_delete")}
          >
            <ActionIcon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  if (!isAllowed("webservice.delegate")) {
    return (
      <div className="Page AccountPage">
        <div className="Page-content AccountPage-content">
          <PageHeader
            breadcrumbs={buildBreadcrumbs(t, "profileDelegate")}
            title={t("common.forbiden")}
            className="PageHero--forbidden"
            flash={message}
          />
        </div>
      </div>
    );
  }

  if (!delegateUsersLoaded) {
    return renderPageShell(
      t("profile.delegate.title"),
      t("profile.delegate.list_intro_loading"),
      <Loader />
    );
  }

  if (delegateUsers.length === 0) {
    return renderPageShell(
      t("profile.delegate.title"),
      t("profile.delegate.list_intro_empty"),
      <>
        <div className="DelegateUser-add">
          <Link to={BASE_PATH + "/profile/delegate/create"} className="Button">
            {t("profile.delegate.add_user")}
          </Link>
        </div>
        <section className="DelegateUser-list">{t("profile.delegate.no_access")}</section>
      </>
    );
  }

  return renderPageShell(
    t("profile.delegate.title"),
    t("profile.delegate.list_intro_default"),
    <>
      <div className="DelegateUser-add">
        <Link to={BASE_PATH + "/profile/delegate/create"} className="Button">
          {t("profile.delegate.add_user")}
        </Link>
      </div>
      <section className="DelegateUser-list">
        <DataTable
          className="DelegateUser-table"
          columns={columns as unknown as DataTableColumn[]}
          rows={delegateUsers as unknown as Record<string, unknown>[]}
          emptyMessage={t("profile.delegate.no_access")}
        />
      </section>
    </>
  );
}

export default compose(
  withTranslation(),
  (Component: React.ComponentType<ProfileDelegatePageListProps>) =>
    withProfileDelegateData(Component, { delegateUser: false }),
  withModulesData
)(ProfileDelegatePageList);
